import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, CheckCircle, XCircle, Lock } from 'lucide-react';
import { PasswordStrengthChecklist, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [tokenState, setTokenState] = useState('checking'); // 'checking'|'valid'|'invalid'|'expired'|'used'
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setTokenState('invalid'); return; }
    axios.get(`${API}/auth/validate-reset-token?token=${encodeURIComponent(token)}`)
      .then(r => {
        if (r.data.valid) {
          setTokenState('valid');
        } else {
          // Map reason to specific state for better messaging
          setTokenState(r.data.reason === 'expired' ? 'expired' : r.data.reason === 'already_used' ? 'used' : 'invalid');
        }
      })
      .catch(() => setTokenState('invalid'));
  }, [token]);

  const [redirectTo, setRedirectTo] = useState('/partner/login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordPolicy(form.password)) { setError('Password must have 8+ chars, uppercase, lowercase, number, and special character'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: form.password });
      if (res.data.redirect_to) setRedirectTo(res.data.redirect_to);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setSaving(false);
    }
  };

  if (tokenState === 'checking') return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm w-full max-w-sm p-8">
        {(tokenState === 'invalid' || tokenState === 'expired' || tokenState === 'used') ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-7 w-7 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">
              {tokenState === 'expired' ? 'Link Expired' : tokenState === 'used' ? 'Link Already Used' : 'Invalid Link'}
            </h2>
            <p className="text-sm text-slate-500">
              {tokenState === 'expired'
                ? 'This password reset link has expired (valid for 10 minutes). Please request a new one.'
                : tokenState === 'used'
                ? 'This reset link has already been used. If you need to reset again, please request a new link.'
                : 'This password reset link is no longer valid. Please request a new one.'}
            </p>
            <Link to="/forgot-password">
              <Button className="w-full mt-2">Request New Link</Button>
            </Link>
          </div>
        ) : done ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Password updated!</h2>
            <p className="text-sm text-slate-500">You can now log in with your new password.</p>
            <Button className="w-full mt-2" onClick={() => navigate(redirectTo)}>Go to Login</Button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Set new password</h1>
              <p className="text-sm text-slate-500 mt-1">Choose a strong password for your account.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min. 8 chars, upper, lower, number, special"
                    value={form.password}
                    onChange={e => { setForm(f => ({ ...f, password: e.target.value })); setError(''); }}
                    className="pr-10"
                    data-testid="rp-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrengthChecklist password={form.password} />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="Re-enter password"
                  value={form.confirm}
                  onChange={e => { setForm(f => ({ ...f, confirm: e.target.value })); setError(''); }}
                  data-testid="rp-confirm-input"
                />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={saving} data-testid="rp-submit-btn">
                {saving ? 'Saving...' : 'Update Password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
